<template>
  <div class="controls">
    <div class="controls__start">
         <HelpTip>
      Snapshots can import and export dataset data.
      A restore process re-imports to dataset from a snapshot, while a create process takes dataset data and export to a snapshot.
      Snapshots can be uploaded or downloaded (MCAP / Appache Arrow).
    </HelpTip>

    <PageControl
      :modelValue="currentPage"
      :totalPages="totalPages"
      @update:modelValue="onPageChange"
    />
    <div class="snapshot-pagination__count">
      {{ allSnapshots.length }} of {{ totalCount ?? allSnapshots.length }}
    </div>

    </div>

 
 
    <div class="controls__end">
      <div class="controls__actions">
        <button class="button" @click="getSnapshots">
          <SVGIcon
            :iconName="'refresh'"
            :width="'18px'"
            :height="'18px'"
          />Refresh
        </button>
        <button
          class="button"
          :class="{ highlight: showFilterPanel }"
          @click="showFilterPanel = !showFilterPanel"
          title="Toggle Filters"
        >
          <v-icon start>mdi-filter</v-icon>
          Filter
          <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
        </button>
        <button class="button" @click="addSnapshot">
          <SVGIcon
            :iconName="'add'"
            :width="'18px'"
            :height="'18px'"
          />
          From Dataset
        </button>
        <button
          class="button"
          aria-label="From File"
          :title="'Add From File'"
          :disabled="parsingFiles"
          :class="{'button-spinner': parsingFiles}"
          @click="chooseSourceFolder"
        >
          <SVGIcon
            :iconName="'import'"
            :width="'18px'"
            :height="'18px'"
          />
          <input
            v-show="false"
            id="import-script-folder-input"
            ref="importFolderInput"
            type="file"
            multiple
            @change="handleFileInput"
          >
          <span>From File</span>
        </button>
      </div>
    </div>
  </div>

  <br>
  <hr>

  <div
    class="data-table scrollbar"
    :class="{_highlight: dropHighlighted}"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent=""
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-card-content">
        <InlineLoader :width="'60px'" :height="'60px'" :border="'8px'" />
        <p>Loading Snapshots</p>
      </div>
    </div>
    <v-data-table
      :headers="headers"
      :items="filteredSnapshots"
      :items-per-page="countPerPage"
      :page="currentPage"
      fixed-header
      density="comfortable"
    >
      <template #headers="{ columns }">
        <tr>
          <template v-for="column in columns" :key="column.key">
            <th class="th2">
              <span class="mr-2">{{ column.title }}</span>
            </th>
          </template>
        </tr>
      </template>
      <template v-for="(header, i) in headers" :key="i" #[`item.${header.key}`]="{ item, index }">
        <template v-if="header.key ==='date'">
          {{ formatTime(item[header.key]) }}
        </template>
        <template v-else-if="header.key ==='description'">
          <div class="td-name-cell" :active="item.newName || item.newName === ''">
            <template v-if="!item.newName && item.newName !== ''">
              <div
                class="text"
                @click.stop.prevent="edit(index)"
              >
                {{ item[header.key] }}
              </div>
              <v-btn
                :icon="'mdi-pencil'"
                size="x-small"
                variant="plain"
                class="edit-btn"
                active-color="#250E81"
                @click.stop.prevent="edit(index)"
              />
            </template>
            <template v-else>
              <input
                ref="snapshotNameEditors"
                v-model="item.newName"
                type="text"
                class="text"
                @blur="updateName(item)"
                @keyup.enter="updateName(item)"
              >
              <v-btn
                id="send-btn"
                :icon="'mdi-check'"
                size="x-small"
                variant="plain"
                class="edit-btn"
                active-color="#250E81"
                @click.stop.prevent="updateName(item)"
              />
            </template>
          </div>
        </template>
        <template v-else-if="header.key ==='status'">
          <span v-if="item[header.key] === 'available' || item[header.key] === ''" class="status-tag _success">Available</span>
          <span v-else-if="item[header.key] === 'unavailable'" class="status-tag _unavailable">Not Available</span>
        </template>
        <template v-else-if="header.key ==='files'">
          <div v-if="item.meta?.file_list && item.meta.file_list.length > 0" class="files-list">
            <span v-for="(file, idx) in item.meta.file_list" :key="idx" class="file-badge">{{ file }}</span>
          </div>
          <span v-else>-</span>
        </template>
        <template v-else-if="header.key ==='snapshot_uid'">
          <EncodedIdField
            :id="item.id"
            type="snapshot"
            :hideLabel="true"
          />
        </template>
        <template v-else-if="header.key ==='dataset_uid'">
          <span
            v-if="item.dataset_id"
            class="snapshot-dataset-link"
            @click="gotoDataset(item.dataset_id)"
          >
            <EncodedIdField
              :id="item.dataset_id"
              type="dataset"
              :hideLabel="true"
            />
          </span>
          <span v-else>-</span>
        </template>
        <template v-else-if="header.key ==='username'">
          <UserIcon
            v-if="item[header.key]"
            :user="userList.find((e) => e.username === item[header.key])"
            :size="'32px'"
          />
        </template>
        <template v-else-if="header.key ==='actions'">
          <BaseMenu
            :placement="'bottom-start'"
            :appendToBody="true"
            @closed="show_menu = false"
          >
            <SVGIcon
              class="actions"
              :iconName="'more_vert'"
              :width="'22px'"
              :height="'22px'"
            />
            <template #menu="{closeMenu}">
              <ul>
                <li>
                  <BaseMenuButton :disabled="disableSnapshot(item)" @click.stop="downloadSnapshot(item),closeMenu()">
                    <SVGIcon
                      title="Download"
                      :iconName="'download'"
                      :width="'22px'"
                      :height="'22px'"
                    />
                    <span>Download</span>
                  </BaseMenuButton>
                </li>
                <li>
                  <BaseMenuButton :disabled="disableSnapshot(item)" @click.stop="restoreSnapshot(item), closeMenu()">
                    <SVGIcon
                      title="Restore snapshot"
                      :iconName="'file_restore'"
                      :width="'22px'"
                      :height="'22px'"
                    />
                    <span>Restore</span>
                  </BaseMenuButton>
                </li>
                <li>
                  <BaseMenuButton class="remove-icon" @click.stop="handleRemoveSnapshot(item), closeMenu()">
                    <SVGIcon
                      title="Remove snapshot"
                      :iconName="'close'"
                      :width="'22px'"
                      :height="'22px'"
                    />
                    <span>Remove</span>
                  </BaseMenuButton>
                </li>
              </ul>
            </template>
          </BaseMenu>
        </template>
        <template v-else>
          {{ item[header.key] }}
        </template>
      </template>
      <template #bottom />
    </v-data-table>
  </div>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonText="buttonText"
    :buttonClass="buttonClass"
    @confirmed="confirmFunction"
  />
  <SnapshotAddModal
    ref="addSnapshotModal"
    :mode="mode"
    :snapshot="modalSnapshot"
    :projects="modalProjects"
    @restore-snapshot="getSnapshots"
  />
  <AppStartModal
    ref="restoreAppModal"
    :appName="restoreAppName"
    :defaultValues="restoreAppDefaultValues"
  />

  <!-- Filter panel -->
  <div :class="['snapshot-filter-panel', { 'snapshot-filter-panel--open': showFilterPanel }]">
    <div class="snapshot-filter-panel__header">
      <h3>Filters</h3>
      <div class="snapshot-filter-panel__actions">
        <button
          type="button"
          class="clear-filters-btn"
          :disabled="activeFilterCount === 0"
          @click="clearAllFilters"
        >Clear All</button>
        <button class="snapshot-filter-panel__close" @click="showFilterPanel = false">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
    </div>
    <div class="snapshot-filter-panel__content">
      <SnapshotFilters
        v-model="snapshotFilters"
        :users-list="uniqueUsernames"
        :status-list="[{ key: 'Available', value: 'available' }, { key: 'Unavailable', value: 'unavailable' }]"
        :extensions-list="formattedExtensions"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref, onBeforeMount, computed, nextTick, watch,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import { getTimeDifference } from '@/assets/js/utils';
import ConfirmModal from '@/components/ConfirmModal.vue';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseMenuButton from '@/components/BaseMenuButton.vue';
import SnapshotAddModal from '@/components/Snapshots/SnapshotAddModal.vue';
import AppStartModal from '@/components/Apps/AppStartModal.vue';
import PageControl from '@/components/DatasetComponent/GalleryComponent/PageControl.vue';
import FilterEntry from '@/components/FilterEntry.vue';
import FilterPopper from '@/components/FilterPopper.vue';
import BaseFilterButton from '@/components/BaseFilterButton.vue';
import SnapshotFilters from '@/components/Snapshots/SnapshotFilters.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import { goto_dataset_gallery } from '@/assets/js/links.js';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import { v4 as uuidv4 } from 'uuid';
import UserIcon from '@/components/UserIcon.vue';
import HelpTip from '@/components/HelpTip.vue';
import InlineLoader from '@/components/InlineLoader.vue';
import useTaskModal from '@/composables/useTaskModal.js';

const $toast = useToast();

const store = useStore();

const dataConnect = new DatastoreConnect();
const headers = ref([
  {
    title: 'User', key: 'username', width: '1%', align: 'center',
  },
  {
    title: 'ID', key: 'snapshot_uid', width: '4%', align: 'start',
  },
  {
    title: 'Name', key: 'description', width: '20%', align: 'start',
  },
 
  {
    title: 'Status', key: 'status', width: '10%', align: 'start',
  },
  {
    title: 'Files', key: 'files', width: '15%', align: 'start',
  },
  {
    title: 'Created', key: 'date', width: '10%', align: 'start',
  },
   {
    title: 'Dataset', key: 'dataset_uid', width: '4%', align: 'start',
  },
  {
    title: '', key: 'actions', width: '1%', align: 'start',
  },
]);
const allSnapshots = ref([]);
const isLoading = ref(false);
const parsingFiles = ref(false);
const files = ref([]);
const fileExtensions = ref([]);
const show_menu = ref(false);
const confirmModal = ref(null);
const addSnapshotModal = ref(null);
const restoreAppModal = ref(null);
const restoreAppName = ref('edgefirst-importer');
const restoreAppDefaultValues = ref({});
const confirmMessage = ref('');
const confirmMessageHeader = ref('');
const buttonText = ref('');
const buttonClass = ref('button-delete');
const confirmFunction = ref(null);
const mode = ref('add');
const modalSnapshot = ref(null);
const snapshotNameEditors = ref([]);
const dropHighlighted = ref(false);
const importFolderInput = ref(null);
const sortDateAsc = ref(false);
const sortStateAsc = ref(false);
const modalProjects = ref([]);
const showFilterPanel = ref(false);
const totalCount = ref(null);
const countPerPage = ref(10);
const offset = ref(0);

const currentPage = computed(() =>
  totalCount.value ? Math.floor(offset.value / countPerPage.value) + 1 : 1,
);

const totalPages = computed(() =>
  totalCount.value ? Math.max(1, Math.ceil(totalCount.value / countPerPage.value)) : 1,
);
const snapshotFilters = ref({
  startDate: '',
  endDate: '',
  dynamicFilters: {},
});

const apiUsernames = ref([]);

const uniqueUsernames = computed(() => {
  // Prefer usernames from API response; fall back to extracting from loaded snapshots
  if (apiUsernames.value.length > 0) {
    return [...apiUsernames.value].sort();
  }
  const users = new Set();
  (allSnapshots.value || []).forEach((s) => { if (s.username) users.add(s.username); });
  return Array.from(users).sort();
});

const formattedExtensions = computed(() => {
  // Convert string array to { key, value } objects for StatusFilter
  return fileExtensions.value.map((ext) => ({ key: ext, value: ext }));
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (snapshotFilters.value.startDate) count += 1;
  if (snapshotFilters.value.endDate) count += 1;
  Object.values(snapshotFilters.value.dynamicFilters || {}).forEach((v) => {
    if ((Array.isArray(v) && v.length > 0) || (typeof v === 'string' && v.trim())) count += 1;
  });
  return count;
});

// Server handles all filtering and pagination — pass through directly
const filteredSnapshots = computed(() => allSnapshots.value || []);

function clearAllFilters() {
  snapshotFilters.value = { startDate: '', endDate: '', dynamicFilters: {} };
  offset.value = 0;
}

function onPageChange(newPage) {
  offset.value = (Number(newPage) - 1) * countPerPage.value;
  getSnapshots();
}

function onCountPerPageChange(e) {
  countPerPage.value = Number(e.target.value);
  offset.value = 0;
  getSnapshots();
}
const projects = computed(() => store.state.projects.projectList);
const writableProjects = ref([]);
const userList = computed(() => store.state.user.userList);

const {
  pauseGetTasks,
  resumeGetTasks,
  isGettingTasks,
  getTasks,
} = useTaskModal();

watch(snapshotFilters, () => {
  offset.value = 0;
  getSnapshots();
}, { deep: true });

async function getSnapshots() {
  isLoading.value = true;
  const filter = {};
  const d = snapshotFilters.value.dynamicFilters || {};
  if (d.name) filter.name = d.name;
  if (d.username?.length) filter.username = d.username;
  if (d.status?.length) filter.status = d.status[0];
  if (d.file_ext?.length) filter.file_ext = d.file_ext;
  if (snapshotFilters.value.startDate) filter.start_date = snapshotFilters.value.startDate;
  if (snapshotFilters.value.endDate) filter.end_date = snapshotFilters.value.endDate;

  const params = { limit: countPerPage.value, offset: offset.value };
  if (Object.keys(filter).length > 0) params.filter = filter;

  await dataConnect.filterSnapshots(params)
    .then((data) => {
      if (data.result && !data.error) {
        const list = data.result.list ?? data.result;
        allSnapshots.value = (Array.isArray(list) ? list : []).sort((a, b) => b.id - a.id);
        allSnapshots.value = allSnapshots.value.map((snapshot) => ({
          ...snapshot,
          newName: null,
        }));
        // Read total count from filter metadata returned alongside list
        const meta = data.result.filter ?? data.result.summary ?? null;
        if (meta?.total !== undefined) totalCount.value = meta.total;
        else if (meta?.count !== undefined) totalCount.value = meta.count;
        // Populate username list from API filter metadata
        if (Array.isArray(meta?.usernames)) {
          apiUsernames.value = meta.usernames;
        } else if (Array.isArray(meta?.users)) {
          apiUsernames.value = meta.users;
        }
        // Populate extension list from API filter metadata
        if (Array.isArray(meta?.file_ext)) {
          fileExtensions.value = meta.file_ext;
        }
      }
    })
    .catch((error) => {
      console.log(error);
      return '';
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function gotoDataset(datasetId) {
  goto_dataset_gallery(datasetId);
}

function disableSnapshot(snapshot) {
  if (snapshot && (snapshot["status"] === 'available' || snapshot["status"] === '')) {
    return false;
  } else if (snapshot && snapshot["status"] === 'unavailable') {
    return true;
  } else {
    return true;
  }
}

function formatTime(str) {
  return `${new Date(str).toLocaleTimeString('default', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
  })}`;
}

function chooseSourceFolder() {
  importFolderInput.value.click();
}

async function handleFileInput(event) {
  const selectedFiles = Array.from(event.target.files);
  try {
    await compileFiles(selectedFiles);
  } catch (e) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Error uploading files: ${e}`,
      type: "error",
      position: "top",
    });
  }
}

async function processFolder(directoryHandle) {
  const tree = { [directoryHandle.name]: {} };
  files.value = [];
  await handleDirectory(directoryHandle, tree, directoryHandle.name);
  try {
    const fileObjs = await Promise.all(files.value);
    await compileFiles(fileObjs);
  } catch (e) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Error uploading files: ${e}`,
      type: "error",
      dismissible: true,
      position: "top",
    });
  }
}

async function compileFiles(fileList) {
  parsingFiles.value = true;
  files.value = fileList;

  let relativePath = "";
  if (files.value && files.value.length > 0) {
    relativePath = files.value[0].relativePath || files.value[0].webkitRelativePath;
  }

  let dirName = "";
  if (relativePath && relativePath !== "") {
    // From folder select
    dirName = relativePath.split("/")[0];
  } else {
    dirName = files.value[0].name;
    dirName = dirName.slice(0, dirName.lastIndexOf('.')) || dirName;
  }
  await uploadFiles(files.value, dirName).then(() => {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Files uploaded successfully`,
      type: 'success',
      position: 'top',
    });
    getSnapshots();
    parsingFiles.value = false;
  }).catch((e) => {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Error uploading files: ${e}`,
      type: 'error',
      dismissible: true,
      position: 'top',
    });
    parsingFiles.value = false;
  });
}

async function handleDirectory(dirhandle, tree, relativePath) {
  const subtree = {};
  tree[dirhandle.name] = subtree;
  for await (const [name, handle] of dirhandle) {
    if (handle.kind === "directory") {
      const newRelativePath = relativePath ? `${relativePath}/${name}` : name;
      await handleDirectory(handle, subtree, newRelativePath);
    } else {
      subtree[name] = "[file]";
      files.value.push(new Promise((resolve, reject) => {
        handle.getFile().then((file) => {
          file.relativePath = `${relativePath}/${name}`;
          resolve(file);
        });
      }));
    }
  }
}

async function uploadFiles(fileList, name) {
  let snapshot_id = 0;
  const fileNames = fileList.map((file) => file.name);
  const fileSizes = fileList.map((file) => file.size);
  const newFileList = [];
  try {
    const response = await dataConnect.createSnapshotUploadMultipartUrl({
      keys: fileNames,
      snapshot_name: name,
      file_sizes: fileSizes,
    });
    if (response && response.result) {
      const firstKey = Object.keys(response.result)[0];
      const directoryPath = firstKey.substring(0, firstKey.lastIndexOf("/"));
      snapshot_id = response.result["snapshot_id"];
      fileList.forEach((file, i) => {
        newFileList.push({
          file,
          size: fileSizes[i],
          urls: response.result[`${directoryPath}/${file.name}`]["urls"],
          uploadId: response.result[`${directoryPath}/${file.name}`]["upload_id"],
          key: `${directoryPath}/${file.name}`,
        });
      });
    }
  } catch (err) {
    throw new Error(`Error creating uploading urls: ${err}`);
  }

  const totalSize = fileSizes.reduce((acc, size) => acc + size, 0); // Calculate total size of all files

  let currentTime = 0;
  await dataConnect.getCurrentTime()
    .then((resp) => {
      if (resp.result) {
        currentTime = resp.result;
      }
    });

  const dbTask = await dataConnect.addDockerTask({
    name: `Upload Snapshot`,
    docker_id: '',
    type: 'upload',
    status: 'running',
    date: currentTime,
    data: {
      stage_definitions: [
        { upload_snapshot: "Uploading Files to Cloud Storage" },
      ],
    },
  }).then((resp) => {
    if (resp.result) {
      return resp.result;
    }
    throw Error("Failed to create import task in database");
  })
    .catch((error) => {
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Error creating task in database: ${error}`,
        type: 'error',
        dismissible: true,
        position: 'top',
      });
    });

  getTasks();
  if (!isGettingTasks.value) {
    resumeGetTasks();
  }

  const sizeMap = {};
  const fileMap = {};
  const totalParts = newFileList.reduce((total, file) => total + file["urls"].length, 0);
  let completedPart = 0;
  let current_percentage = 0;
  const uploadPromises = newFileList.map((fileObj) => uploadFileMultipart(
    fileObj.file,
    fileObj.urls,
    fileObj.uploadId,
    (fileName, fileEvent, totalFileParts, partIndex) => {
      if (!(fileName in sizeMap)) {
        sizeMap[fileName] = Array(totalFileParts).fill(0);
      }
      sizeMap[fileName][partIndex] += (fileEvent.loaded - sizeMap[fileName][partIndex]);
      const countSum = Object.values(sizeMap)
        .flat() // Flatten the arrays into a single array
        .filter((num) => !Number.isNaN(num)) // Filter out NaN values
        .reduce((sum, num) => sum + num, 0); // Sum up all the numbers

      if (Math.floor((countSum / totalSize) * 100) > current_percentage) {
        current_percentage = Math.floor((countSum / totalSize) * 100);
        dataConnect.updateTaskStage({
          docker_task_id: dbTask.id,
          stage: "upload_snapshot",
          status: "uploading",
          percentage: current_percentage,
        });
      }
    },
    async (PartNumber, ETag, fileName, uploadId, totalFileParts) => {
      completedPart += 1;
      if (!(fileName in fileMap)) {
        fileMap[fileName] = {
          uploadedData: [],
          totalFileParts,
          uploadId,
        };
      }
      fileMap[fileName]["uploadedData"].push({ PartNumber, ETag });
      if (fileMap[fileName]["uploadedData"].length === fileMap[fileName]["totalFileParts"]) {
        const completeResponse = await dataConnect.completeSnapshotUploadMultipartUrl({
          key: fileObj.key,
          upload_id: fileObj.uploadId,
          etag_list: fileMap[fileName]["uploadedData"].sort((a, b) => a.PartNumber - b.PartNumber),
        });
        if (completeResponse && completeResponse.error) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Error uploading files: ${completeResponse.error.message}`,
            type: 'error',
            dismissible: true,
            position: 'top',
          });
        }
      }
      if (completedPart === totalParts) {
        dataConnect.updateTaskStage({
          docker_task_id: dbTask.id,
          stage: "upload_snapshot",
          status: "complete",
          percentage: 100,
        });
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Snapshot files uploaded successfully`,
          type: 'success',
          position: 'top',
        });
        parsingFiles.value = false;
        getTasks();
        updateStatus(snapshot_id, "available");
      }
    },
    (error) => {
      dataConnect.updateTaskStage({
        docker_task_id: dbTask.id,
        stage: "upload_snapshot",
        label: "Uploading Files to Cloud Storage",
        message: `Error: ${error}`,
        status: "error",
        percentage: current_percentage,
      });
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Error uploading files: ${error}`,
        type: 'error',
        position: 'top',
        dismissible: true,
      });
      parsingFiles.value = false;
    },
  ));

  getSnapshots();
  await Promise.all(uploadPromises);
}

function uploadFileMultipart(file, presignedUrls, uploadId, progressCallback, partCompleteCallback, errorCallback) {
  return new Promise((resolve, reject) => {
    const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB per part
    const totalParts = presignedUrls.length;

    for (let i = 0; i < totalParts; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const presignedUrl = presignedUrls[i];

      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl, true);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const fileEvent = event;
          progressCallback(file.name, fileEvent, totalParts, i);
        }
      });

      xhr.onload = () => {
        if (xhr.status === 200) {
          const etag = xhr.getResponseHeader("ETag");
          partCompleteCallback(i + 1, etag.replace(/^"|"$/g, ''), file.name, uploadId, totalParts);
        }
      };

      xhr.onerror = (e) => {
        errorCallback(e);
      };

      xhr.send(chunk);
    }
  });
}
function addSnapshot() {
  mode.value = 'add';
  modalProjects.value = projects.value;
  addSnapshotModal.value.showModal();
}

async function restoreSnapshot(snapshot) {
  const snapshotFiles = snapshot.meta?.file_list || [];
  const normalizedFiles = snapshotFiles.map((file) => String(file).toLowerCase());
  const hasZip = normalizedFiles.some((file) => file.endsWith('.zip'));
  const hasMcap = normalizedFiles.some((file) => file.endsWith('.mcap'));

  if (hasZip) {
    restoreAppName.value = 'edgefirst-importer';
  } else if (hasMcap) {
    restoreAppName.value = 'mcap-importer';
  } else {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'No restorable files in the snapshot',
      type: 'error',
      position: 'top',
    });
    return;
  }

  restoreAppDefaultValues.value = {
    snapshot_id: snapshot.id,
    snapshot: snapshot.id,
    snapshot_name: snapshot.description || snapshot.name || '',
  };
  await nextTick();
  restoreAppModal.value.showModal(snapshot.id, snapshot.description || snapshot.name || '');
}

function handleRemoveSnapshot(snapshot) {
  confirmMessage.value = `Are you sure you want to permanently remove snapshot "${snapshot.description}"?`;
  confirmMessageHeader.value = 'Remove Snapshot';
  buttonText.value = 'Remove';
  buttonClass.value = 'button-delete';
  confirmFunction.value = () => {
    removeSnapshot(snapshot);
  };
  confirmModal.value.showModal();
}

async function removeSnapshot(snapshot) {
  await dataConnect.removeSnapshot({
    snapshot_id: parseInt(snapshot.id),
  }).then(() => {
    getSnapshots();
  }).catch((e) => {
    $toast.open({
      queue: true,
      duration: 5000,
      dismissible: true,
      message: e,
      type: 'error',
      position: 'top',
    });
  });
}

async function getProjectList() {
  await dataConnect.getProjectList({ get_num_datasets: true })
    .then((data) => {
      const projectList = data.result;
      let noLT = 0;
      let noDS = 0;
      let noTR = 0;
      let noVA = 0;
      let noAU = 0;

      projectList.forEach((proj) => {
        noAU += proj.no_of_automations;
        noLT += proj.no_of_label_tasks;
        noDS += proj.no_of_datasets;
        noTR += proj.no_of_trainers;
        noVA += proj.no_of_validations;
      });

      for (let k = 0; k < projectList.length; k++) {
        projectList[k].total_automations = noAU;
        projectList[k].total_datasets = noDS;
        projectList[k].total_trainers = noTR;
        projectList[k].total_validations = noVA;
        projectList[k].total_label_tasks = noLT;
      }
      store.commit('projects/setProjectList', projectList);
    })
    .catch((e) => { console.log(e); });
}

async function getWritableProjectList() {
  return dataConnect.getProjectList({ only_writable: true })
    .then((data) => {
      if (data.error) {
        throw data.error;
      }
      return data.result;
    })
    .catch((e) => { console.log(e); });
}

function findProjectName(v) {
  let result = '-';
  if (projects.value && projects.value.length > 0) {
    for (let i = 0; i < projects.value.length; i += 1) {
      if (projects.value[i].id === v.project_id) {
        result = projects.value[i].name;
      }
    }
  }
  return result;
}

function edit(index) {
  allSnapshots.value[index].newName = allSnapshots.value[index].description;
  nextTick(() => {
    snapshotNameEditors.value.focus();
  });
}

async function updateName(snapshot) {
  if (snapshot.newName && snapshot.newName !== snapshot.description) {
    await dataConnect.updateSnapshot({
      snapshot_id: parseInt(snapshot.id),
      description: snapshot.newName,
    }).then(() => {
      snapshot.description = snapshot.newName;
      getSnapshots();
      snapshot.newName = null;
    }).catch((e) => {
      $toast.open({
        queue: true,
        duration: 5000,
        message: e,
        type: 'error',
        dismissible: true,
        position: 'top',
      });
    });
  } else {
    snapshot.newName = null;
  }
}

async function updateStatus(snapshot_id, status) {
  await dataConnect.updateSnapshot({
    snapshot_id,
    status,
  }).then(() => {
    getSnapshots();
  }).catch((e) => {
    $toast.open({
      queue: true,
      duration: 5000,
      message: e,
      type: 'error',
      dismissible: true,
      position: 'top',
    });
  });
}

function handleDragEnter() {
  dropHighlighted.value = true;
}
function handleDragLeave() {
  dropHighlighted.value = false;
}

async function handleDrop(e) {
  if (parsingFiles.value) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Error uploading files: Previous file upload in progress`,
      type: 'error',
      dismissible: true,
      position: 'top',
    });
    dropHighlighted.value = false;
  } else {
    dropHighlighted.value = false;
    const filePromises = [];
    let folderPromise = null;

    const items = [...e.dataTransfer.items];

    const entries = await Promise.all(
      items.map(async (item) => (item.kind === "file" ? item.getAsFileSystemHandle() : null)),
    );

    for (const entry of entries) {
      if (entry) {
        if (entry.kind === "file") {
          filePromises.push(entry.getFile());
        } else if (entry.kind === "directory") {
          folderPromise = processFolder(entry);
          break;
        }
      }
    }

    if (folderPromise) {
      await Promise.all([folderPromise]);
    }

    if (filePromises.length > 0) {
      const fileObjs = await Promise.all(filePromises);
      await compileFiles(fileObjs);
    }
  }
}

async function downloadSnapshot(snapshot) {
  await dataConnect.createSnapshotDownloadUrl({
    snapshot_id: parseInt(snapshot.id),
  }).then(async (data) => {
    if (data && data.result) {
      for (const [key, url] of Object.entries(data.result)) {
        await downloadFile(url, key);
      }
    } else if (data && data.error) {
      $toast.open({
        queue: true,
        duration: 5000,
        dismissible: true,
        message: data.error.message,
        type: 'error',
        position: 'top',
      });
    }
  }).catch((e) => {
    $toast.open({
      queue: true,
      duration: 5000,
      dismissible: true,
      message: e,
      type: 'error',
      position: 'top',
    });
  });
}

async function downloadFile(url, key) {
  return new Promise((resolve) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = key;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(resolve, 1000); // Wait before next download
  });
}

onBeforeMount(() => {
  getSnapshots();
  if (!projects.value || (projects.value && projects.value.length === 0)) {
    getProjectList();
  }
  if (!writableProjects.value || (writableProjects.value && writableProjects.value.length === 0)) {
    getWritableProjectList()
      .then((writableProjectsResp) => {
        writableProjects.value = writableProjectsResp;
      });
  }
});

defineExpose({
  getSnapshots,
});

</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

a {
  text-decoration: none;
  @include themify() {
      color: themed('body-text-color');
    }
}
.data-table {
  width: calc(100% - 40px);
  height: 100%;
  padding: 0 15px 15px 15px;
  border-radius: 8px;
  border: 3px solid transparent;
  margin: 0 20px 20px 20px;
  overflow-y: auto;
  position: relative;

  th + th:not(:nth-last-of-type(-n+1)) {
    border-left: 2px solid rgb(74, 76, 75);
    border-image: linear-gradient(to bottom, rgba(108,219,141,0) 25%,rgba(74, 76, 75,1) 25%,rgba(74, 76, 75,1) 75%,rgba(108,219,141,0) 75%);
    border-image-slice: 1;
  }

  &._highlight {
    border: 3px dashed #250E81;
    background: #250e8125;
  }

}

.loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.loading-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  height: 100%;

  p {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
    color: #333;
  }

  :deep(.center-screen) {
    min-height: auto;
    height: auto;
  }
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  p {
    margin: 0;
    font-size: 1rem;
    color: #666;
  }
}

:deep(.v-table) {
  background: transparent;
  border-radius: 0;
}

.th2 {
  background: transparent !important;
  span {
    font-size: 1.0rem;
    font-weight: 600;
  }
}

.select-all {
  display: flex;
  flex-direction: row;
  width: 15px;
}

.label-icon {
  display: flex;
  flex-direction: row;
  align-items: center;
  color: var(--color-black) !important;

  :nth-child(2) {
    margin-left: 5px;
  }
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 600;
  white-space: nowrap;
  @include themify() {
    color: themed('color-black');
  }

  &._running {
    @include themify() {
      color: themed('color-status-complete');
    }
  }
  &._pending {
    @include themify() {
      color: themed('color-status-running');
    }
  }
  &._success {
    @include themify() {
      color: themed('color-status-complete');
    }
  }
  &._error {
    @include themify() {
      color: themed('color-status-error');
    }
  }
  &._unavailable {
      color: var(--gray-600)
  }

  div {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }
}

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.button {
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 26px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  padding: 4px 8px;
  gap: 2px;
  width: fit-content;

  @include themify() {
    color: themed('body-text-color')
  }

  &:hover {
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }
}

.file-read {
  color: var(--color-success);
  font-size: 0.9rem;
  font-weight: 600;

  &.parse-fail {
    color: var(--color-error);
  }
}

:deep(.v-data-table__tr) {
  td:first-child:hover {
    background: var(--color-white-800);
  }
  &:hover {
    background: rgba(140,140,140,0.2)
  }
}

.actions {
  cursor: pointer;
  color: grey;
  &:hover {
    @include themify() {
      color: themed('icon-color-primary');
      background-color: themed('icon-hover-color');
      border-radius: 100%;
      box-shadow: 0 0 0 4px themed('icon-hover-color');
    }
  }
}

.remove-icon {
  color: var(--color-error);
}

.td-name-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  min-height: 25px;
  padding: 0 0 0 0;

  &[active="true"] .edit-btn, &:hover .edit-btn {
    visibility: visible !important;
  }

  .chkbx-input {
    display: none;
  }

  .text {
    margin-left: 5px;
    font-size: 0.9rem;
    width: 170px;
    border-bottom: 1px solid transparent;
    cursor: text;
    flex: 1 1 auto;
    font-weight: 500;
  }

  input[type="text"] {
    margin-left: 5px;
    width: 170px;
    font-size: 0.9rem;
    border-bottom: 1px solid transparent;
    outline: none;

    &:focus {
      border-bottom: 1px solid #250E81;
    }
  }

  .edit-btn {
    visibility: hidden;
  }
}

.snapshot-dataset-link {
  cursor: pointer;
  &:hover {
    @include themify() {
      color: themed('color-primary');
    }
    text-decoration: underline;
  }
}

.files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.file-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  background: white;
  border: 1px solid #ccc;
  color: #333;
}

.cursor-pointer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0 4px;
  background: #250E81;
  color: #fff;
}

.highlight {
  @include themify() {
    background: rgba(37, 14, 129, 0.1) !important;
    border-color: #250E81 !important;
    color: #250E81 !important;
  }
}

.snapshot-filter-panel {
  position: fixed;
  right: 0;
  top: 0;
  height: 100%;
  width: 0;
  background: white;
  border-left: 1px solid #e0e0e0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease-in-out;
  overflow: hidden;
  z-index: 100;
  display: flex;
  flex-direction: column;

  &--open {
    width: 320px;
    overflow-y: auto;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;

    h3 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  &__content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
}

.snap-filter-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.snap-filter-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
}

.snap-filter-input {
  height: 32px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 0.82rem;
  outline: none;
  width: 100%;
  @include themify() {
    border: 1px solid #d0d0d0;
    background: #ffffff;
    color: #333;
  }
  &:focus {
    border-color: #250E81;
  }
}

.clear-filters-btn {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #555;
  border-radius: 4px;
  background: transparent;
  color: #555;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.snapshot-pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  @include themify() {
    border-top: 1px solid #e0e0e0;
    background: #fafafa;
  }

  &__count {
    font-size: 0.82rem;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 4px;
    @include themify() {
      background: rgba(0, 0, 0, 0.05);
      color: #444;
    }
  }

  &__size {
    height: 28px;
    padding: 0 6px;
    font-size: 0.78rem;
    border-radius: 4px;
    @include themify() {
      border: 1px solid #d0d0d0;
      background: #ffffff;
      color: #333;
    }
  }
}
</style>

<style lang="scss">
/* Unscoped — applies inside FilterPopper portal */
.filter-settings {
  display: flex;
  flex-direction: column;
  padding: 8px;
  border-radius: inherit;
  gap: 6px;
  min-width: 200px;
  overflow: auto;

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    input[type=radio],
    input[type=checkbox] {
      margin-top: 3px;
      flex-shrink: 0;
    }
  }
}

.filter-footer {
  min-height: 38px;
  width: 100%;
  border-top: 1px solid rgba(140, 140, 140, 0.375);
  padding: 8px;
  text-align: center;

  .clear-button {
    text-align: center;
    @include themify() {
      color: themed('color-danger');
    }
  }
}

.filter-input {
  width: 100%;
  height: 30px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 0.82rem;
  outline: none;
  @include themify() {
    border: 1px solid #d0d0d0;
    background: #ffffff;
    color: #333;
  }
  &:focus {
    @include themify() {
      border-color: #250E81;
    }
  }
}
</style>
